import React, { useState, useMemo } from 'react';
import { Modal, Select, TreeSelect, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import {
  SpaceRelation,
  ASSIGN_USER_TO_SPACE,
  AssignUserToSpaceVariables,
  AssignUserToSpaceResponse,
  AUTH_API_URL,
} from './SpaceAssignments';
import { GET_SITE_HIERARCHY } from '@/hooks/useSiteOptions';
import { SiteOption } from '@/hooks/useSiteHierarchyFilters';
import {
  flattenHierarchyToSpaceOptions,
  buildTreeDataWithSelectAll,
  getDefaultExpandedKeys,
  getSpaceIdsToAssign,
} from './SpaceTreeUtils';

const { Option } = Select;

interface AddSpaceModalProps {
  open: boolean;
  userId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export const AddSpaceModal: React.FC<AddSpaceModalProps> = ({
  open,
  userId,
  onCancel,
  onSuccess,
}) => {
  const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
  const [relation, setRelation] = useState<SpaceRelation>('can_view');

  // Fetch real site hierarchy from main Butlr API (not auth API)
  const { data: hierarchyData, loading: hierarchyLoading } = useQuery(GET_SITE_HIERARCHY, {
    skip: !open, // Only fetch when modal is open
  });

  // Flatten hierarchy into space options
  const availableSpaces = useMemo(() => {
    const sites = (hierarchyData?.sites?.data || []) as SiteOption[];
    return flattenHierarchyToSpaceOptions(sites);
  }, [hierarchyData]);

  // Build tree with "Select All Spaces" root node
  const treeData = useMemo(() => buildTreeDataWithSelectAll(availableSpaces), [availableSpaces]);

  // Expand sites and buildings by default (show up to floor level, rooms collapsed)
  const defaultExpandedKeys = useMemo(
    () => getDefaultExpandedKeys(availableSpaces),
    [availableSpaces],
  );

  const [assignToSpace, { loading }] = useMutation<
    AssignUserToSpaceResponse,
    AssignUserToSpaceVariables
  >(ASSIGN_USER_TO_SPACE, {
    context: { uri: AUTH_API_URL },
  });

  const handleAdd = async () => {
    const spacesToAssign = getSpaceIdsToAssign(selectedSpaces, availableSpaces);
    if (spacesToAssign.length === 0) {
      message.error('Please select a space');
      return;
    }

    const results = await Promise.allSettled(
      spacesToAssign.map((space) =>
        assignToSpace({
          variables: {
            input: {
              user_id: userId,
              space_type: space.level,
              space_id: space.id,
              relation,
            },
          },
        }),
      ),
    );

    const failedSpaces = results
      .map((r, i) => (r.status === 'rejected' ? spacesToAssign[i].id : null))
      .filter(Boolean);

    if (failedSpaces.length > 0 && failedSpaces.length < spacesToAssign.length) {
      message.warning(
        `${spacesToAssign.length - failedSpaces.length} space(s) assigned, but ${
          failedSpaces.length
        } failed.`,
      );
    } else if (failedSpaces.length === spacesToAssign.length) {
      message.error('Failed to assign spaces');
      return;
    } else {
      message.success(
        spacesToAssign.length === 1
          ? 'Space assignment added'
          : `${spacesToAssign.length} space assignments added`,
      );
    }
    setSelectedSpaces([]);
    setRelation('can_view');
    onCancel();
    onSuccess();
  };

  const handleClose = () => {
    setSelectedSpaces([]);
    setRelation('can_view');
    onCancel();
  };

  return (
    <Modal
      title="Assign Space"
      open={open}
      onCancel={handleClose}
      onOk={handleAdd}
      okText="Assign"
      cancelText="Cancel"
      confirmLoading={loading || hierarchyLoading}
      okButtonProps={{ className: '!bg-black !text-white hover:!bg-gray-700 !border-black' }}
      width={480}
    >
      <div className="bg-gray-50 rounded-lg p-4 mt-4">
        <div className="flex items-center gap-2">
          <TreeSelect
            treeData={treeData}
            value={selectedSpaces}
            onChange={(value) =>
              setSelectedSpaces(Array.isArray(value) ? value : value ? [value] : [])
            }
            placeholder={hierarchyLoading ? 'Loading spaces...' : 'Select a space'}
            treeCheckable
            showCheckedStrategy={TreeSelect.SHOW_PARENT}
            treeDefaultExpandedKeys={defaultExpandedKeys}
            className="flex-1"
            loading={hierarchyLoading}
            disabled={hierarchyLoading}
            maxTagCount={3}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            treeIcon
          />
          <Select value={relation} onChange={(value) => setRelation(value)} style={{ width: 120 }}>
            <Option value="can_view">Can View</Option>
            <Option value="can_edit">Can Edit</Option>
          </Select>
        </div>
      </div>
    </Modal>
  );
};

export default AddSpaceModal;
